import {produce} from 'immer';
import {isEqual, throttle} from 'lodash';
import {useCallback, useEffect, useRef, useState} from 'react';
import {Dot} from '@/map/components/Overlay';
import {palette} from '@/styles';
import {Marker, Polyline} from '../../Overlay';
import {RulerInfoCursorOptions, RulerItem} from './index.d';
import {InfoCursor} from './InfoCursor';
import {RulerCursor} from './RulerCursor';
import {RulerOverlay} from './RulerOverlay';

interface IProps {
  map: any;
  borderColor?: string;
  name?: string;
  infoOptions?: RulerInfoCursorOptions;
  startPoint?: naver.maps.LatLng;
  onComplete?(distance: RulerItem): void;
  onCancel?(): void;
}

const COLOR = palette.teal200;

export const MeasureDistance = ({
  map,
  name,
  borderColor = COLOR,
  infoOptions,
  startPoint,
  onComplete,
  onCancel,
}: IProps) => {
  const evt = useRef<{[name: string]: naver.maps.MapEventListener}>({});

  const [point, setPoint] = useState<naver.maps.LatLng | undefined>(startPoint);
  const [path, setPath] = useState<naver.maps.LatLng[]>([]);
  const [onEscape, setOnEscape] = useState<boolean>(false);
  const [accDistance, setAccDistance] = useState<number>(0);
  const [mileStons, setMileStons] = useState<
    Array<{coord: naver.maps.LatLng; distance: number}>
  >([]);
  const [guidePoint, setGuidePoint] = useState<naver.maps.LatLng | undefined>();
  const [guideDistance, setGuideDistance] = useState<number>(0);

  const updatePath = useCallback(
    (coord: naver.maps.LatLng) => {
      if (path.length > 0) {
        const distance = map
          .getProjection()
          .getDistance(path[path.length - 1], coord);

        setMileStons(
          produce((draft) => {
            draft.push({coord, distance});
          })
        );
        setAccDistance((prev) => prev + distance);
      }

      setPath(
        produce((draft) => {
          draft.push(coord);
        })
      );
      setGuideDistance(0);
    },
    [map, path, setPath, setMileStons, setAccDistance, setGuideDistance]
  );

  const onKeypress = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' || e.key === 'Esc') {
      setOnEscape(true);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onClickEvent = useCallback(
    throttle((e: {originalEvent: MouseEvent; coord: naver.maps.LatLng}) => {
      if (!map || e?.originalEvent?.button !== 0) {
        return;
      }

      const prevCenter = map.getCenter();

      setTimeout(() => {
        const nextCenter = map.getCenter();

        if (isEqual(prevCenter, nextCenter)) {
          setPoint(e.coord);
        }
      }, 100);
    }, 500),
    [map, setPoint]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onMousemoveEvent = useCallback(
    throttle((e: {coord: naver.maps.LatLng}) => {
      setGuidePoint(e.coord);
    }, 50),
    []
  );

  useEffect(() => {
    if (!map) {
      return;
    }

    evt.current.mousedown = naver.maps.Event.addListener(
      map,
      'mousedown',
      onClickEvent
    );

    evt.current.rightclick = naver.maps.Event.addListener(
      map,
      'rightclick',
      () => setOnEscape(true)
    );

    document.addEventListener('keydown', onKeypress);

    return () => {
      document.removeEventListener('keydown', onKeypress);

      Object.keys(evt.current).map((name) => {
        const event = evt.current[name];

        if (event) {
          naver.maps.Event.removeListener(event);
        }

        return false;
      });

      evt.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (point) {
      updatePath(point);

      if (!evt.current.mousemove) {
        evt.current.mousemove = naver.maps.Event.addListener(
          map,
          'mousemove',
          onMousemoveEvent
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [point]);

  useEffect(() => {
    if (onEscape) {
      setGuidePoint(undefined);
      setGuideDistance(0);
      setAccDistance(0);
      setPoint(undefined);

      if (path.length > 1) {
        onComplete?.({
          path,
          mileStons,
        });
      } else {
        onCancel?.();
      }

      setPath([]);
      setMileStons([]);
      setOnEscape(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onEscape]);

  useEffect(() => {
    if (guidePoint && map && path.length) {
      setGuideDistance(
        map.getProjection().getDistance(path[path.length - 1], guidePoint)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guidePoint]);

  return (
    <>
      {!path.length && (
        <RulerCursor
          map={map}
          hint={name ?? '거리 측정'}
          borderColor={borderColor}
          color={palette.gray600}
        />
      )}
      {!!guidePoint && (
        <>
          <InfoCursor
            map={map}
            distance={accDistance + guideDistance}
            position={guidePoint ?? path[path.length - 1]}
            options={infoOptions}
            showEscapeInfo
          />
          <Marker
            map={map}
            position={guidePoint}
            draggable={false}
            clickable={false}
          >
            <Dot borderWidth={4} borderColor={borderColor} />
          </Marker>
          {path.length > 0 && (
            <Polyline
              map={map}
              path={[path[path.length - 1], guidePoint]}
              clickable={false}
              strokeColor={borderColor}
              strokeWeight={4}
              strokeOpacity={0.5}
            />
          )}
        </>
      )}
      <RulerOverlay
        map={map}
        item={{
          path,
          mileStons,
          id: 0,
        }}
        infoOptions={infoOptions}
        borderColor={borderColor}
      />
    </>
  );
};
